import React, { useMemo, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Spinner,
  CardHeader,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import { USER_PATHS } from 'routes/routesConstants';
import {
  useAssignStaffShopMutation,
  useChangeStaffRoleMutation,
  useCreateStaffMutation,
  useStaffDetailQuery,
  useUnassignStaffShopMutation,
  useUpdateStaffMutation,
} from 'http-client/hooks/useStaffHooks';
import { CreateStaffDto, UpdateStaffDto } from 'http-client/types/staff.types';
import { useRolesListQuery } from 'http-client/hooks/useRoleHooks';

interface UserFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  roleId: string;
  assignedShopId: string;
  password?: string;
}

interface ShopOption {
  id: string;
  name: string;
}

const CreateOrUpdateUserForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { data: staffData, isLoading: loadingStaff } = useStaffDetailQuery(id ?? '');
  const { data: shops } = useListQuery<ShopOption>(API_ENDPOINTS.TENANT.SHOPS.BASE);
  const { data: roles } = useRolesListQuery();

  const { mutate: createStaff, isPending: creating } = useCreateStaffMutation();
  const { mutate: updateStaff, isPending: updating } = useUpdateStaffMutation();
  const { mutate: changeRole } = useChangeStaffRoleMutation();
  const { mutate: assignShop } = useAssignStaffShopMutation();
  const { mutate: unassignShop } = useUnassignStaffShopMutation();

  const roleOptions = useMemo(
    () =>
      (roles ?? []).map((role) => ({
        id: role.id,
        name: role.name,
      })),
    [roles],
  );

  const initialValues: UserFormValues = {
    name: staffData?.name ?? '',
    email: staffData?.email ?? '',
    phoneNumber: staffData?.phoneNumber ?? '',
    roleId: staffData?.role?.id ?? '',
    assignedShopId: staffData?.assignedShop?.id ?? '',
    password: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().trim().required('Name is required'),
    email: Yup.string()
      .trim()
      .email('Email must be a valid email address')
      .required('Email is required'),
    phoneNumber: Yup.string().trim().required('Phone number is required'),
    roleId: Yup.string().trim().required('Role is required'),
    assignedShopId: Yup.string().trim(),
    password: isEditMode
      ? Yup.string().trim()
      : Yup.string()
          .trim()
          .required('Password is required')
          .min(8, 'Password must be at least 8 characters'),
  });

  const formik = useFormik<UserFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<UserFormValues>) => {
      const onSuccess = () => navigate(USER_PATHS.BASE);
      const onError = (error: unknown) => handleFormErrors<UserFormValues>(error, formikHelpers);

      if (isEditMode && id && staffData) {
        const updatePayload: UpdateStaffDto = {
          name: values.name.trim(),
          email: values.email.trim(),
          phoneNumber: values.phoneNumber.trim(),
        };

        updateStaff(
          { id, data: updatePayload },
          {
            onSuccess: () => {
              const roleChanged = values.roleId && values.roleId !== staffData.role?.id;
              const shopChanged =
                (values.assignedShopId || '') !== (staffData.assignedShop?.id ?? '');

              if (!roleChanged && !shopChanged) {
                onSuccess();
                return;
              }

              let pending = 0;
              let completed = 0;
              const checkDone = () => {
                completed += 1;
                if (completed >= pending) onSuccess();
              };

              if (roleChanged) {
                pending += 1;
                changeRole({ id, body: { roleId: values.roleId } }, { onError, onSettled: checkDone });
              }

              if (shopChanged) {
                pending += 1;
                if (values.assignedShopId) {
                  assignShop(
                    { id, body: { shopId: values.assignedShopId } },
                    { onError, onSettled: checkDone },
                  );
                } else {
                  unassignShop({ id }, { onError, onSettled: checkDone });
                }
              }
            },
            onError,
          },
        );
        return;
      }

      const createPayload: CreateStaffDto = {
        name: values.name.trim(),
        email: values.email.trim(),
        phoneNumber: values.phoneNumber.trim(),
        password: values.password?.trim() ?? '',
        roleId: values.roleId,
        assignedShopId: values.assignedShopId || null,
      };

      createStaff(createPayload, { onSuccess, onError });
    },
  });

  if (isEditMode && loadingStaff) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md={12} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">
              {isEditMode ? 'Update Employee' : 'Add New Employee'}
            </h2>
          </CardHeader>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Row>
                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="name">Full Name</Label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className={`form-control ${
                        formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                      }`}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Full Name"
                      required
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="invalid-feedback">{formik.errors.name}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="email">Email</Label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className={`form-control ${
                        formik.touched.email && formik.errors.email ? 'is-invalid' : ''
                      }`}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Email"
                      required
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="invalid-feedback">{formik.errors.email}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="phoneNumber">Phone Number</Label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="text"
                      className={`form-control ${
                        formik.touched.phoneNumber && formik.errors.phoneNumber ? 'is-invalid' : ''
                      }`}
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Phone Number"
                      required
                    />
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                      <div className="invalid-feedback">{formik.errors.phoneNumber}</div>
                    )}
                  </FormGroup>
                </Col>

                {!isEditMode && (
                  <Col sm={12} md={6} lg={6}>
                    <FormGroup>
                      <Label for="password">Password</Label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        className={`form-control ${
                          formik.touched.password && formik.errors.password ? 'is-invalid' : ''
                        }`}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Password"
                        required
                      />
                      {formik.touched.password && formik.errors.password && (
                        <div className="invalid-feedback">{formik.errors.password}</div>
                      )}
                    </FormGroup>
                  </Col>
                )}

                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="roleId">Role</Label>
                    <select
                      id="roleId"
                      name="roleId"
                      className={`form-control ${
                        formik.touched.roleId && formik.errors.roleId ? 'is-invalid' : ''
                      }`}
                      value={formik.values.roleId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    >
                      <option value="">Select Role</option>
                      {roleOptions.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.roleId && formik.errors.roleId && (
                      <div className="invalid-feedback">{formik.errors.roleId}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={6} lg={6}>
                  <FormGroup>
                    <Label for="assignedShopId">Assigned Shop</Label>
                    <select
                      id="assignedShopId"
                      name="assignedShopId"
                      className={`form-control ${
                        formik.touched.assignedShopId && formik.errors.assignedShopId
                          ? 'is-invalid'
                          : ''
                      }`}
                      value={formik.values.assignedShopId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Shop (optional)</option>
                      {shops?.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.name}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={creating || updating}>
                  {creating || updating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Employee'
                    : 'Create Employee'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(CreateOrUpdateUserForm);
